// Standalone full-bleed Sankey page for the PJ Daily Solution.
//
// Renders the full-viewport layout matching the interactive mockup signed off
// in document review dcc6974f: floating HUD (toolbar + chip strip + day pills)
// pinned to the top, supply-chain Sankey fills the rest of the viewport with
// vertical-only zoom + focus-mode reflow, right-edge rail of collapsed tabs
// for the side panels (critical / dropped / supply mix), and a draggable /
// resizable / minimizable floating detail panel that appears when a node or
// link is clicked.
//
// Composes the existing dailyView sub-components — only the layout shell
// differs from the in-tab dashboard.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CplexModelerChatPanel } from '../components/cplex/CplexModelerChatPanel';
import { VersionDeltaModal } from '../components/cplex/VersionDeltaModal';
import { VersionDetailsModal } from '../components/cplex/VersionDetailsModal';
import {
  getRun,
  getVersionNode,
  listRuns,
  listVersionNodes,
  TERMINAL_STATES,
  type CplexRun,
  type CplexRunSummary,
  type OpenFillTicketMap,
  type OrderScoreMap,
  type VersionNodeDetail,
  type VersionNodeSummary,
} from '../services/cplex';
import { SankeyChart, type SankeyPayload } from '../components/cplex/SankeyChart';
import { SummaryChips } from '../components/cplex/dailyView/SummaryChips';
import { DayFilter } from '../components/cplex/dailyView/DayFilter';
import { CriticalPOPanel } from '../components/cplex/dailyView/CriticalPOPanel';
import { UnfilledPanel } from '../components/cplex/dailyView/UnfilledPanel';
import { RmDonut } from '../components/cplex/dailyView/RmDonut';
import { SankeyDetailPanel } from '../components/cplex/dailyView/SankeyDetailPanel';
import {
  buildDetailContent,
  buildSankeyFromMPP,
  chipStats,
  filterRowsByModelDay,
  STAGES,
} from '../components/cplex/dailyView/mppHelpers';
import type { MPPRow, SelectedTarget } from '../components/cplex/dailyView/types';
import { FloatingPanel } from '../components/cplex/dailyView/FloatingPanel';
import { SankeySearchBar } from '../components/cplex/dailyView/SankeySearchBar';
import { FilterChips } from '../components/cplex/dailyView/FilterChips';
import { StageToggle } from '../components/cplex/dailyView/StageToggle';
import { useSankeyFilters } from '../components/cplex/dailyView/useSankeyFilters';
import { buildSearchIndex } from '../components/cplex/dailyView/searchIndex';
import { PriorityCoverageControls } from '../components/cplex/dailyView/PriorityCoverageControls';
import { FillTicketPanel } from '../components/cplex/dailyView/FillTicketPanel';
import {
  applyDailyViewFilters,
  coverageStats,
} from '../components/cplex/dailyView/dailyFilters';
import { ChartErrorBoundary } from '../components/cplex/dailyView/ChartErrorBoundary';
import { VersionBrowserModal } from '../components/cplex/VersionBrowserModal';
import { EMPTY_DAILY_FILTERS } from '../components/cplex/dailyView/types';
import type { DailyViewFilterState } from '../components/cplex/dailyView/types';
import { GuruFeedbackWidget } from '../components/layout/GuruFeedbackWidget';
import type { Stage } from '../components/cplex/dailyView/mppHelpers';
import styles from './CplexSankeyFullPage.module.css';

type ActiveTab = 'crit' | 'unfilled' | 'donut' | 'fill' | 'detail' | null;

export function CplexSankeyFullPage() {
  const params = useParams<{ runId?: string }>();
  const [runs, setRuns] = useState<CplexRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(
    params.runId ?? null,
  );
  const [run, setRun] = useState<CplexRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // MVA v2 — CPLEX Modeler version lineage overlay.
  const [versionNodes, setVersionNodes] = useState<VersionNodeSummary[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [versionNode, setVersionNode] = useState<VersionNodeDetail | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [versionRefreshKey, setVersionRefreshKey] = useState(0);
  const navigate = useNavigate();

  // Fetch the list of completed runs (for the run picker) and the active run.
  useEffect(() => {
    let cancel = false;
    void (async () => {
      try {
        const res = await listRuns(undefined, 25, 0);
        if (cancel) return;
        const completed = res.items.filter((r) => TERMINAL_STATES.has(r.state));
        setRuns(completed);
        if (!selectedRunId && completed.length > 0) {
          setSelectedRunId(completed[0].id);
        }
      } catch (e: unknown) {
        const msg = (e as { message?: string })?.message ?? 'Failed to load runs.';
        if (!cancel) setErr(msg);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedRunId]);

  useEffect(() => {
    if (!selectedRunId) {
      setRun(null);
      return;
    }
    let cancel = false;
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        // Fast first paint: scope master_production_plan to the default model.
        // The dashboard renders one model at a time, but the MPP table carries
        // all four variants (~3.3x the rows shown), so scoping lets the server
        // serialise + gzip ~1/3 the payload and the client parse far less.
        // '5_day' is the default view (see activeModel in the dashboard below).
        const scoped = await getRun(
          selectedRunId,
          'master_production_plan,sankey',
          '5_day',
          'scores,fill_tickets',
        );
        if (!cancel) setRun(scoped);
      } catch (e: unknown) {
        const msg = (e as { message?: string })?.message ?? 'Failed to load run.';
        if (!cancel) setErr(msg);
        return;
      } finally {
        if (!cancel) setLoading(false);
      }
      // Then stream in every model in the background so switching the model
      // dropdown is instant. Non-fatal — the scoped view already works, and the
      // sankey object lists all models so the dropdown is complete immediately.
      try {
        const full = await getRun(
          selectedRunId,
          'master_production_plan,sankey',
          undefined,
          'scores,fill_tickets',
        );
        if (!cancel) setRun(full);
      } catch {
        /* keep the fast scoped view — switching just waits on this retry */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedRunId]);

  // MVA v2: load the model's version lineage once a run (hence a model) is loaded.
  useEffect(() => {
    const modelId = run?.model_id;
    if (!modelId) {
      setVersionNodes([]);
      return;
    }
    let cancel = false;
    void (async () => {
      try {
        const items = await listVersionNodes(modelId);
        if (!cancel) setVersionNodes(items);
      } catch {
        if (!cancel) setVersionNodes([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [run?.model_id, versionRefreshKey]);

  // Fetch the selected version's detail (its result_tables drive the Sankey).
  useEffect(() => {
    if (!selectedVersionId) {
      setVersionNode(null);
      return;
    }
    let cancel = false;
    void (async () => {
      try {
        const node = await getVersionNode(selectedVersionId);
        if (!cancel) setVersionNode(node);
      } catch {
        if (!cancel) setVersionNode(null);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedVersionId]);

  // Prefer the selected CPLEX-Modeler version's tables; fall back to the run's.
  // Flip the version selector back to "Base run" for a quick A/B comparison.
  const mppRows = useMemo<MPPRow[] | null>(() => {
    const raw =
      (versionNode?.result_tables?.master_production_plan as unknown) ??
      run?.result_tables?.master_production_plan;
    if (!Array.isArray(raw)) return null;
    return raw as unknown as MPPRow[];
  }, [run, versionNode]);

  const fallbackPayload = useMemo<SankeyPayload | null>(() => {
    const raw = run?.result_tables?.sankey ?? run?.result_summary?.sankey;
    return (raw as SankeyPayload | undefined) ?? null;
  }, [run]);

  if (loading && !run) {
    return <div className={styles.loading}>Loading run…</div>;
  }
  if (err) {
    return <div className={styles.error}>{err}</div>;
  }
  if (!run) {
    return <div className={styles.loading}>No run selected.</div>;
  }
  if (!mppRows) {
    return (
      <div className={styles.loading}>
        This run has no <code>master_production_plan</code> — the full-bleed view
        needs PJ Daily Solution output. Use the in-tab Sankey view for older
        runs.
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 44,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 12px',
          background: '#0f1720',
          color: '#e5edf5',
          borderBottom: '1px solid #22323f',
          zIndex: 200,
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span style={{ opacity: 0.8 }}>Version</span>
          <select
            value={selectedVersionId ?? ''}
            onChange={(e) => setSelectedVersionId(e.target.value || null)}
            style={{
              font: 'inherit',
              color: '#0f172a',
              background: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              padding: '3px 6px',
              maxWidth: 380,
            }}
          >
            <option value="" style={{ color: '#0f172a', background: '#fff' }}>
              Base run
            </option>
            {versionNodes.map((v) => {
              const pct = v.summary_deltas?.objective_pct as number | undefined;
              const delta =
                typeof pct === 'number'
                  ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)`
                  : '';
              const created = v.created_at
                ? ` · ${new Date(v.created_at).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}`
                : '';
              return (
                <option
                  key={v.id}
                  value={v.id}
                  style={{ color: '#0f172a', background: '#fff' }}
                >
                  {v.label} [{v.state}]{delta}{created}
                </option>
              );
            })}
          </select>
        </label>
        <button
          type="button"
          onClick={() => setBrowseOpen(true)}
          title="Browse all versions — dates, assumptions & constraints"
          style={{
            fontSize: 12,
            cursor: 'pointer',
            background: 'none',
            border: '1px solid #2a3b57',
            color: 'inherit',
            borderRadius: 5,
            padding: '2px 8px',
          }}
        >
          ⧉ Browse
        </button>
        {versionNode ? (
          <span style={{ fontSize: 12, opacity: 0.85, display: 'flex', alignItems: 'center', gap: 6 }}>
            Showing version: {versionNode.label}
            <button
              type="button"
              onClick={() => setDetailsOpen(true)}
              title="Show this version's constraints & assumptions"
              style={{
                fontSize: 12,
                cursor: 'pointer',
                background: 'none',
                border: '1px solid #2a3b57',
                color: 'inherit',
                borderRadius: 5,
                padding: '1px 6px',
              }}
            >
              ⓘ details
            </button>
          </span>
        ) : null}
        {versionNode ? (
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            title="A/B compare this version against its parent (objective, orders, day-spread)"
            style={{ fontSize: 13, cursor: 'pointer' }}
          >
            ⇄ Compare
          </button>
        ) : null}
        <button
          type="button"
          onClick={() =>
            navigate(
              selectedRunId ? `/cplex/fill-plan/${selectedRunId}` : '/cplex/fill-plan',
            )
          }
          title="Production Fill Plan — finished goods × day 1–5 fill (primary view)"
          style={{ marginLeft: 'auto', fontSize: 13, cursor: 'pointer' }}
        >
          ⬒ Fill Plan
        </button>
        <button
          type="button"
          onClick={() => setChatOpen((o) => !o)}
          style={{ fontSize: 13, cursor: 'pointer' }}
        >
          ✎ Edit with CPLEX Modeler
        </button>
        <button
          type="button"
          onClick={() => navigate('/cplex')}
          title="Models, runs & history (admin)"
          aria-label="CPLEX admin"
          style={{
            fontSize: 16,
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            color: 'inherit',
            lineHeight: 1,
          }}
        >
          ⚙
        </button>
      </div>
      <SankeyWorkspace
        runs={runs}
        selectedRunId={selectedRunId}
        onSelectRun={setSelectedRunId}
        rows={mppRows}
        fallbackPayload={fallbackPayload}
        openFillTickets={run.open_fill_tickets ?? null}
        orderScores={run.order_scores ?? null}
      />
      {chatOpen ? (
        <CplexModelerChatPanel
          onClose={() => setChatOpen(false)}
          onTurnComplete={() => setVersionRefreshKey((k) => k + 1)}
        />
      ) : null}
      {compareOpen && versionNode ? (
        <VersionDeltaModal
          versions={versionNodes}
          initialBeforeId={versionNode.parent_version_id}
          initialAfterId={versionNode.id}
          onClose={() => setCompareOpen(false)}
        />
      ) : null}
      {detailsOpen && versionNode ? (
        <VersionDetailsModal version={versionNode} onClose={() => setDetailsOpen(false)} />
      ) : null}
      {browseOpen ? (
        <VersionBrowserModal
          versions={versionNodes}
          selectedVersionId={selectedVersionId}
          onSelect={setSelectedVersionId}
          onClose={() => setBrowseOpen(false)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// SankeyWorkspace — the actual full-bleed layout once data is loaded.
// ---------------------------------------------------------------------------

function SankeyWorkspace({
  runs,
  selectedRunId,
  onSelectRun,
  rows,
  fallbackPayload,
  openFillTickets,
  orderScores,
}: {
  runs: CplexRunSummary[];
  selectedRunId: string | null;
  onSelectRun: (id: string) => void;
  rows: MPPRow[];
  fallbackPayload: SankeyPayload | null;
  openFillTickets: OpenFillTicketMap | null;
  orderScores: OrderScoreMap | null;
}) {
  const models = useMemo(() => {
    // Start from the rows we actually have (the scoped active-model slice during
    // fast first paint), then union in the sankey object's full model list so
    // the dropdown is complete before the background full-load arrives.
    const out = Array.from(
      new Set(rows.map((r) => r.model_type).filter(Boolean) as string[]),
    );
    const seen = new Set(out);
    for (const m of fallbackPayload?.models ?? []) {
      if (!seen.has(m)) {
        seen.add(m);
        out.push(m);
      }
    }
    return out;
  }, [rows, fallbackPayload]);

  // Prefer the 5_day model when present — it's the most useful daily view
  // (no_pos/full/solon_only often have no multi-stage chain to draw).
  const [activeModel, setActiveModel] = useState<string>(
    () => (models.includes('5_day') ? '5_day' : models[0] ?? '5_day'),
  );
  const [activeDay, setActiveDay] = useState<string>('all');
  const [dvFilters, setDvFilters] =
    useState<DailyViewFilterState>(EMPTY_DAILY_FILTERS);
  const [selected, setSelected] = useState<SelectedTarget | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>(null);
  const [minimized, setMinimized] = useState(false);
  // Per-column visibility. Default to every Sankey stage on; the toggle row
  // below the chart lets the user hide / show individual columns.
  const [visibleStages, setVisibleStages] = useState<Set<Stage>>(
    () => new Set(STAGES),
  );
  const visibleStagesList = useMemo<Stage[]>(
    () => STAGES.filter((s) => visibleStages.has(s)),
    [visibleStages],
  );
  const toggleStage = (s: Stage) => {
    setVisibleStages((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        // Keep at least one column visible — toggling off the last one would
        // leave the chart with nothing to render.
        if (next.size === 1) return prev;
        next.delete(s);
      } else {
        next.add(s);
      }
      return next;
    });
    setSelected(null);
  };

  const sliceRows = useMemo(
    () => filterRowsByModelDay(rows, activeModel, activeDay),
    [rows, activeModel, activeDay],
  );

  // Priority + fill-ticket-coverage narrowing layered on top of the model+day
  // slice. With the default (Show all / 0 / 0) state this is an identity pass,
  // so search / sankey / panels behave exactly as before until the user acts.
  const viewRows = useMemo(
    () => applyDailyViewFilters(sliceRows, dvFilters, openFillTickets, orderScores),
    [sliceRows, dvFilters, openFillTickets, orderScores],
  );
  const covStats = useMemo(
    () => coverageStats(sliceRows, openFillTickets),
    [sliceRows, openFillTickets],
  );
  const hasTickets =
    !!openFillTickets && Object.keys(openFillTickets).length > 0;

  // Search index spans the active model+day slice so chips only surface
  // entities the user can actually see on the current chart. Rebuilding is
  // cheap (~one pass over the slice rows) and keeps suggestions truthful.
  const searchEntities = useMemo(() => buildSearchIndex(viewRows), [viewRows]);
  const filters = useSankeyFilters(viewRows);

  // Dropped-orders panel ↔ chart sync. A row click toggles an `order` filter
  // chip for that order, so the highlight rides the same path as the search
  // bar (chip strip shows it, N-of-M pill updates, chart dims the rest).
  const focusedOrders = useMemo(
    () =>
      new Set(
        filters.chips.filter((c) => c.kind === 'order').map((c) => c.label),
      ),
    [filters.chips],
  );
  const toggleOrderFocus = (order: string) => {
    const entity = searchEntities.find(
      (e) => e.kind === 'order' && e.label === order,
    );
    if (!entity) return;
    if (focusedOrders.has(order)) filters.remove(entity);
    else filters.add(entity);
  };

  const sankey = useMemo<SankeyPayload>(() => {
    const built = buildSankeyFromMPP(viewRows, visibleStagesList);
    return {
      models: [activeModel],
      // Drive the chart's column layout from the user's visibility selection
      // — hidden stages get pruned from both the data and the column axis.
      stages: visibleStagesList,
      by_model: {
        [activeModel]: {
          nodes: built.nodes,
          links: built.links,
          totals: {
            lbs: built.links.reduce((a, l) => a + l.lbs, 0),
            orders: new Set(viewRows.map((r) => r.order).filter(Boolean)).size,
            links: built.links.length,
            nodes: built.nodes.length,
          },
        },
      },
    };
  }, [viewRows, activeModel, visibleStagesList]);

  const stats = useMemo(
    () => chipStats(rows, activeModel, activeDay),
    [rows, activeModel, activeDay],
  );

  const detailContent = useMemo(() => {
    if (!selected) return null;
    const model = sankey.by_model[activeModel];
    if (!model) return null;
    const laidNodes = model.nodes.map((n) => ({
      id: n.id,
      stage: n.stage,
      label: n.label,
    }));
    const laidLinks = model.links
      .map((l) => {
        const s = model.nodes.find((n) => n.id === l.source);
        const t = model.nodes.find((n) => n.id === l.target);
        if (!s || !t) return null;
        return {
          source: { id: s.id, stage: s.stage, label: s.label },
          target: { id: t.id, stage: t.stage, label: t.label },
          lbs: l.lbs,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
    const hiNodes = new Set<string>();
    if (selected.kind === 'node') {
      const queue = [selected.nodeId];
      hiNodes.add(selected.nodeId);
      const visit = (id: string, dir: 'down' | 'up') => {
        for (const l of laidLinks) {
          const from = dir === 'down' ? l.source.id : l.target.id;
          const to = dir === 'down' ? l.target.id : l.source.id;
          if (from === id && !hiNodes.has(to)) {
            hiNodes.add(to);
            queue.push(to);
          }
        }
      };
      while (queue.length) {
        const cur = queue.shift()!;
        visit(cur, 'down');
        visit(cur, 'up');
      }
    }
    return buildDetailContent(selected, viewRows, laidLinks, laidNodes, hiNodes);
  }, [selected, sankey, activeModel, viewRows]);

  // Opening a panel tab and clicking a node both flow through the same
  // floating-panel surface. Selecting a node also opens the 'detail' tab.
  const handleSelectChange = (s: SelectedTarget | null) => {
    setSelected(s);
    if (s) {
      setActiveTab('detail');
      setMinimized(false);
    } else if (activeTab === 'detail') {
      setActiveTab(null);
    }
  };

  const toggleTab = (tab: Exclude<ActiveTab, null>) => {
    if (activeTab === tab) {
      setActiveTab(null);
    } else {
      setActiveTab(tab);
      setMinimized(false);
      if (tab !== 'detail') setSelected(null);
    }
  };

  const closePanel = () => {
    setActiveTab(null);
    setSelected(null);
  };

  const critCount = useMemo(
    () =>
      new Set(
        rows
          .filter(
            (r) =>
              r.model_type === activeModel &&
              r.critical_po === 'Yes' &&
              (activeDay === 'all' || String(r.day) === activeDay),
          )
          .map((r) => r.order),
      ).size,
    [rows, activeModel, activeDay],
  );
  const unfilledCount = useMemo(
    () =>
      new Set(
        rows
          .filter(
            (r) => r.model_type === activeModel && r.filled_from === 'Dropped / Unfilled',
          )
          .map((r) => r.order),
      ).size,
    [rows, activeModel],
  );

  return (
    <div className={styles.workspace}>
      {/* Floating HUD pinned at the top. Layout flow:
            1. Run / model picker + day-status badge
            2. Summary KPI chips + day-filter pills
            3. Column toggles (per-stage visibility)
            4. Search bar (below the pills so it's grounded near the chart it
               drives) + the N-of-M flow-count pill when chips are applied
            5. Active filter chip strip (only when chips exist) */}
      <div className={styles.hud}>
        <div className={styles.toolbar}>
          <label className={styles.toolbarLabel}>
            Run:
            <select
              value={selectedRunId ?? ''}
              onChange={(e) => onSelectRun(e.target.value)}
            >
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  {new Date(r.created_at).toLocaleString()} ·{' '}
                  {r.model_name ?? '?'} · v{r.model_version}
                </option>
              ))}
            </select>
          </label>
          {models.length > 1 && (
            <label className={styles.toolbarLabel}>
              Model:
              <select
                value={activeModel}
                onChange={(e) => {
                  setActiveModel(e.target.value);
                  setSelected(null);
                  setActiveDay('all');
                }}
              >
                {models.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          )}
          <span className={styles.toolbarSub}>
            {activeDay === 'all' ? 'All days' : `Day ${activeDay}`} ·{' '}
            {activeModel}
          </span>
        </div>
        <div className={styles.chipBar}>
          <SummaryChips stats={stats} model={activeModel} day={activeDay} />
          <DayFilter
            rows={rows}
            activeModel={activeModel}
            activeDay={activeDay}
            onChange={(d) => {
              setActiveDay(d);
              setSelected(null);
            }}
          />
        </div>
        <PriorityCoverageControls
          state={dvFilters}
          onChange={setDvFilters}
          covered={covStats.covered}
          missing={covStats.missing}
          hasTickets={hasTickets}
        />
        <div className={styles.toggleRow}>
          <StageToggle visible={visibleStages} onToggle={toggleStage} />
        </div>
        <div className={styles.searchRow}>
          <SankeySearchBar
            entities={searchEntities}
            applied={filters.chips}
            onAddFilter={filters.add}
          />
          {filters.chips.length > 0 && (
            <span className={styles.filterCount}>
              {filters.result.rowIndices.size} of {viewRows.length} flows
            </span>
          )}
        </div>
        {filters.chips.length > 0 && (
          <div className={styles.filterChipRow}>
            <FilterChips
              chips={filters.chips}
              onRemove={filters.remove}
              onClear={filters.clear}
            />
          </div>
        )}
      </div>

      {/* Full-bleed Sankey canvas */}
      <div className={styles.canvas}>
        <ChartErrorBoundary onReset={() => setDvFilters(EMPTY_DAILY_FILTERS)}>
          <SankeyChart
            payload={sankey}
            activeModel={activeModel}
            embedded
            selected={selected}
            onSelect={handleSelectChange}
            filterHighlightNodeIds={
              filters.result.active ? filters.result.nodeIds : null
            }
          />
        </ChartErrorBoundary>
      </div>

      {/* Right-edge rail of collapsed panel tabs */}
      <div className={styles.rail}>
        <button
          type="button"
          className={`${styles.railTab} ${styles.railTabWarn}`}
          onClick={() => toggleTab('crit')}
          title="Critical-PO orders"
        >
          <span className={styles.railIco}>⚠</span>
          <span className={styles.railCnt}>{critCount}</span>
          <span className={styles.railSub}>critical PO</span>
        </button>
        <button
          type="button"
          className={`${styles.railTab} ${styles.railTabBad}`}
          onClick={() => toggleTab('unfilled')}
          title="Dropped / Unfilled"
        >
          <span className={styles.railIco}>✗</span>
          <span className={styles.railCnt}>{unfilledCount}</span>
          <span className={styles.railSub}>dropped</span>
        </button>
        <button
          type="button"
          className={styles.railTab}
          onClick={() => toggleTab('donut')}
          title="Supply mix"
        >
          <span className={styles.railIco}>●</span>
          <span className={styles.railCnt}>mix</span>
          <span className={styles.railSub}>supply rm</span>
        </button>
        <button
          type="button"
          className={`${styles.railTab} ${styles.railTabBad}`}
          onClick={() => toggleTab('fill')}
          title="Fill tickets — covered vs. to-create"
        >
          <span className={styles.railIco}>◷</span>
          <span className={styles.railCnt}>{covStats.missing}</span>
          <span className={styles.railSub}>fill tix</span>
        </button>
      </div>

      {/* Floating panel — draggable, resizable, minimizable */}
      {activeTab && (
        <FloatingPanel
          minimized={minimized}
          onToggleMinimize={() => setMinimized((m) => !m)}
          onClose={closePanel}
          title={panelTitle(activeTab, detailContent?.primaryLabel)}
          wide={activeTab === 'unfilled'}
        >
          {activeTab === 'crit' && (
            <CriticalPOPanel
              rows={rows}
              activeModel={activeModel}
              activeDay={activeDay}
              fill
            />
          )}
          {activeTab === 'unfilled' && (
            <UnfilledPanel
              rows={rows}
              activeModel={activeModel}
              fill
              onFocusOrder={toggleOrderFocus}
              focusedOrders={focusedOrders}
            />
          )}
          {activeTab === 'donut' && <RmDonut rows={viewRows} expanded />}
          {activeTab === 'fill' && (
            <FillTicketPanel
              rows={viewRows}
              tickets={openFillTickets}
              fill
            />
          )}
          {activeTab === 'detail' && detailContent && (
            <SankeyDetailPanel
              content={detailContent}
              onBack={closePanel}
            />
          )}
        </FloatingPanel>
      )}

      {/* GuruCloudAI feedback widget — bottom-right floating logo that opens
          the feature-request / bug-report modal. Lives here because the page
          renders outside MainLayout (where the widget normally mounts). */}
      <GuruFeedbackWidget />
    </div>
  );
}

function panelTitle(tab: Exclude<ActiveTab, null>, detailLabel?: string): string {
  switch (tab) {
    case 'crit':
      return '⚠ Critical-PO orders';
    case 'unfilled':
      return '✗ Dropped / Unfilled';
    case 'donut':
      return 'Supply mix (rm type)';
    case 'fill':
      return '◷ Fill tickets';
    case 'detail':
      return detailLabel ?? 'Node detail';
  }
}

export default CplexSankeyFullPage;
